// Per-mount opt-in observer that watches `.react-flow__node` style.visibility
// transitions inside a given canvas root and emits [chatmap-vis] events.
// The bug-class signal: when the visibility-gate bug regresses, you'll see
// a WRAPPER vis=visible→hidden event after initial measurement with no
// corresponding remount. Absence of that pattern = bug is dead.
//
// Triad-folded constraints (spec rev2 §4.3):
//  - No module-level singleton — each canvas mount gets its own observer.
//    HMR / Strict Mode dev double-invoke runs mount→cleanup→mount; per-mount
//    lifecycle handles every sequence symmetrically.
//  - No document.body fallback — caller passes an explicit canvas root.
//    Avoids the SPA-nav-away footgun (body-scoped observer survives canvas
//    unmount and churns watching the whole document for style mutations).
//  - WeakMap local to each observer instance — GC-correct when the observer
//    disconnects.

import { chatmapLog, isChatmapDebugEnabled } from './chatmapLog';

/**
 * Start watching `rootEl`'s subtree for `.react-flow__node` style.visibility
 * transitions. Returns a cleanup function that disconnects the observer.
 *
 * If debug is disabled at call time, returns a no-op cleanup without
 * attaching anything. To re-evaluate the debug toggle, the caller must
 * unmount + remount the canvas (or flip localStorage.chatmapDebug and
 * reload — the documented operator path).
 */
export function startChatmapVisObserver(rootEl: Element): () => void {
  if (!isChatmapDebugEnabled()) return () => {};

  // Local-to-this-observer; auto-GC'd when obs disconnects + this closure
  // releases the only strong ref to lastVis.
  const lastVis = new WeakMap<Element, string>();

  const obs = new MutationObserver((mutations) => {
    for (const m of mutations) {
      const w = m.target as HTMLElement;
      if (!w.classList?.contains('react-flow__node')) continue;
      if (m.type !== 'attributes' || m.attributeName !== 'style') continue;

      const v = w.style.visibility || 'visible';
      const prev = lastVis.get(w);
      if (prev === v) continue;
      lastVis.set(w, v);

      const id = (w.getAttribute('data-id') ?? '').slice(0, 36);
      const kind = w.className.match(/react-flow__node-(\w+)/)?.[1];

      chatmapLog('vis', `WRAPPER vis=${prev ?? 'init'}→${v}`, {
        id,
        kind,
        w: w.offsetWidth,
        h: w.offsetHeight,
      });
    }
  });

  obs.observe(rootEl, {
    attributes: true,
    attributeFilter: ['style'],
    subtree: true,
  });

  return () => obs.disconnect();
}
